import { Location } from '@/types';
import { Chip, ToggleGroup, ToggleGroupItem, Typography } from '@prenuvo/halo-web';
import { useState } from 'react';
import { Divider } from '..';
import { ExclusionCriteria, InclusionCriteria } from './Criterias';
import PulseAnimation from './PulseAnimation/PulseAnimation';
import { mapResearchCenterNames } from './mapResearchCenterNames';

interface ResearchOnlyLocationProps {
  location: Location | null;
}

const criterias = [
  { id: 1, label: 'Inclusion Criteria' },
  { id: 2, label: 'Exclusion Criteria' },
];

const ResearchOnlyLocation = ({ location }: ResearchOnlyLocationProps) => {
  const [currentCriteria, setCurrentCriteria] = useState(criterias[0]?.id);

  const handleCriteriaChange = (id: string) => {
    setCurrentCriteria(Number(id));
  };

  const { locationCity, researchCenterName, researchCenterAdjective } = mapResearchCenterNames(
    location?.address?.city ?? '',
  );

  return (
    <div>
      <Chip
        leftIcon={{
          'aria-label': 'Pulsating circles animation',
          source: () => <PulseAnimation />,
        }}
        className="pointer-events-none mt-4 rounded-lg border-none bg-orange-100"
      >
        <Typography variant="button-sm" as="p" className="text-orange-600">
          This location is a clinical study location
        </Typography>
      </Chip>
      <Divider />
      <Typography variant="h4-reg" as="p" className="my-6 text-teal-500">
        About Screening in {locationCity}
      </Typography>
      <ul className="ml-6 list-disc leading-7">
        <li>
          {researchCenterName} administers Prenuvo screening whole-body MRI (WB-MRI) scans pursuant
          to a clinical research study protocol.
        </li>
        <li>
          The screening WB-MRI scan technique utilized and the clinical service experienced by
          participants in the study at the {researchCenterName} are otherwise essentially equivalent
          to other Prenuvo sites. Full details of what is covered can be found here (
          <Typography variant="link">
            <a href="https://www.prenuvo.com/conditions" target="_blank">
              Pathologies Prenuvo can scan for
            </a>
          </Typography>{' '}
          ) under “Whole Body”.
        </li>
        <li>
          The study is designed to scientifically measure over time the performance of the Prenuvo
          WB-MRI scan in predicting clinically significant diagnoses; therefore, as part of the
          study, you will be contacted by a {researchCenterAdjective} research team member to check
          in on your health at a follow-up time after your scan.
        </li>
        <li>
          {locationCity}-area screening at the {researchCenterName} is conducted as a pragmatic
          study (mimicking real-world conditions). It is therefore a participant funded study where
          the standard patient should expect to pay USD 2200 to participate. If you&apos;re unable
          to afford the participation cost, there may be options to assist you, please contact the{' '}
          {researchCenterAdjective} team for further info.
        </li>
      </ul>
      {/* This was taken out of OB v2 design */}
      {/* <Container className="sm:flex-column mb-6 mt-4 flex gap-2 border border-blue-700 px-3 py-4 md:flex-row md:items-center">
        <div>
          <Icon className="fill-current text-blue-700" size="md" source={InformationCircleLg} />
        </div> */}
      {/* <div>
          <Typography variant="button-md" as="p" className="leading-6 text-blue-700">
            {locationCity} screening is conducted under a study cost reimbursement model with the
            current cost set at $2,200.
          </Typography>
        </div> */}
      {/* </Container> */}
      <Typography variant="h4-reg" as="p" className="my-6 text-teal-500">
        {researchCenterAdjective} Studies Inclusion & Exclusion Criteria
      </Typography>
      <ToggleGroup
        type="single"
        onValueChange={handleCriteriaChange}
        defaultValue={String(currentCriteria)}
      >
        {criterias.map((criteria, index) => (
          <ToggleGroupItem
            className="px-2 py-4"
            key={index}
            label={{ children: criteria.label }}
            value={String(criteria.id)}
          />
        ))}
      </ToggleGroup>
      {currentCriteria === 1 && <InclusionCriteria />}
      {currentCriteria === 2 && <ExclusionCriteria />}
      <Divider />
    </div>
  );
};

export default ResearchOnlyLocation;
